import Icon from '@/components/UI/Icon';
import { _isNil, _isNotBlank, _notNil } from '@/littledash';
import { type Dispatch, type JSX, useCallback, useEffect, useReducer } from 'react';
import Layout from './Layout';
import { type Step, type StepFormAction, type StepFormState } from './StepForm.model';
import { reducer } from './StepForm.reducer';

interface StepFormProps<T extends StepFormState<T>, PropType extends Record<string, any>> {
  title: string;
  steps: Array<Step<T, PropType>>;
  initialState: T;
  props: PropType;
  finalStepAction: (state: T, dispatch: Dispatch<StepFormAction<T>>) => void;
  horizontal: boolean;
  finalButtonText?: string;
  finalButtonClass?: string;
  cancelFunction?: () => void;
  extraButtonClick?: (state: T, dispatch: Dispatch<StepFormAction<T>>) => void;
  titlePadding?: string;
  disableNav?: boolean;
  errorBanner?: unknown;
  outerStep?: number;
  testPrefix?: string;
  /**
   * When true, pressing Enter advances to the next step (or finishes on the last step)
   * using the same validation rules as the Next/Finish buttons.
   * In textareas, plain Enter advances; Ctrl/Cmd+Enter and Shift+Enter insert a newline.
   */
  nextOnEnter?: boolean;
}

const StepForm = <T extends StepFormState<T>, PropType extends Record<string, any>>({
  title,
  titlePadding,
  steps,
  initialState,
  props,
  finalStepAction,
  finalButtonText,
  finalButtonClass,
  cancelFunction,
  extraButtonClick,
  disableNav,
  horizontal,
  errorBanner,
  outerStep,
  testPrefix = 'stepform',
  nextOnEnter = false,
}: StepFormProps<T, PropType>): JSX.Element => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const nextStep = () => dispatch({ type: 'next' });
  const previousStep = () => dispatch({ type: 'previous' });
  const jumpToStep = (currentStep: number) => dispatch({ type: 'jumpToStep', currentStep });
  const { currentStep, stepReady, finalStepReady = true } = state;
  const step = steps[currentStep];
  const errorMessage = step.isValid(state);
  const isValid = errorMessage === true;
  const isLastStep = currentStep === steps.length - 1;

  useEffect(() => {
    if (_notNil(outerStep)) {
      jumpToStep(outerStep);
    }
  }, [outerStep]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key !== 'Enter' || event.defaultPrevented || event.isComposing) {
        return;
      }

      const target = event.target;
      // The listener is on the document, so ignore keystrokes raised outside this form.
      if (!(target instanceof HTMLElement) || _isNil(target.closest('[data-test-component="StepForm"]'))) {
        return;
      }

      const isMultilineTextSurface = target.tagName === 'TEXTAREA' || target.isContentEditable;

      // Inside multiline fields the modified Enter shortcuts insert a newline instead of navigating.
      if (isMultilineTextSurface && (event.ctrlKey || event.metaKey || event.shiftKey)) {
        return;
      }

      // Let the browser activate focused interactive controls (Back/Next/Finish/Cancel).
      if (target.tagName === 'BUTTON' || target.tagName === 'A') {
        return;
      }

      if (!isValid) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (!isLastStep) {
        if (stepReady === true) {
          dispatch({ type: 'next' });
        }
        return;
      }

      if (finalStepReady === true) {
        finalStepAction(state, dispatch);
      }
    },
    [isValid, isLastStep, stepReady, finalStepReady, finalStepAction, state]
  );

  useEffect(() => {
    if (nextOnEnter !== true || disableNav === true) {
      return;
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [nextOnEnter, disableNav, handleKeyDown]);

  return (
    <div className="h-100 flex flex-column justify-between" data-test-component="StepForm">
      <Layout
        title={title}
        titlePadding={titlePadding}
        steps={steps}
        currentStep={currentStep}
        jumpToStep={jumpToStep}
        horizontal={horizontal}
        errorBanner={errorBanner}
        content={step.renderStep(state, dispatch, props)}
        state={state}
        dispatch={dispatch}
        testPrefix={testPrefix}
      />

      {disableNav !== true && (
        <div className="flex justify-between mt3">
          <div className={`flex justify-start ${isLastStep ? finalButtonClass : ''}`}>
            <button disabled={currentStep === 0} className="plain mr2" onClick={previousStep}>
              Back
            </button>

            {!isLastStep ? (
              <div
                data-tooltip-id="global-tooltip-id"
                data-tooltip-content={typeof errorMessage === 'string' ? errorMessage : undefined}
                data-tooltip-hidden={isValid}
              >
                <button className="mr2" disabled={!isValid || stepReady !== true} onClick={nextStep}>
                  Next
                </button>
              </div>
            ) : (
              <button
                className="mr2"
                disabled={!isValid || finalStepReady !== true}
                onClick={() => finalStepAction(state, dispatch)}
              >
                {_isNotBlank(finalButtonText) ? finalButtonText : 'Finish'}
              </button>
            )}
          </div>
          <div className="flex">
            {_notNil(step.extraButton) && (
              <button onClick={() => extraButtonClick?.(state, dispatch)} className="plain relative mr2">
                <Icon
                  icon={step.extraButton.icon}
                  className="absolute"
                  style={{ left: '6px', top: '10px' }}
                  width="27"
                  height="20"
                />
                <span className="pl3">{step.extraButton.title}</span>
              </button>
            )}
            {_notNil(cancelFunction) && (
              <button className="plain" onClick={cancelFunction}>
                Cancel
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StepForm;
